import { getTransactionAmount } from './utils';

export interface ExpenseBriefingResult {
  totalAmount: number;
  receiptCount: number;
  averageAmount: number;
  topCategory?: string;
  topCategoryAmount?: number;
  topCategoryRatio?: number;
  monthOverMonthDiff?: number;
  budgetUsageRatio?: number;
  summaryMessages: string[];
  insightMessages: string[];
  cautionMessages: string[];
  disclaimer: string;
}

interface BriefingInput {
  receipts: any[]; // Current month transactions
  lastMonthReceipts?: any[]; // Last month transactions
  monthlyBudget?: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  MEDICAL: '병원비',
  FOOD: '사료·간식',
  SUPPLIES: '용품',
  GROOMING: '미용',
  INSURANCE: '보험료',
  OTHER: '기타',
};

function formatDate(dateStr?: string): string {
  if (!dateStr) return '최근';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const year = parts[0];
    const month = parts[1];
    const day = parts[2];
    return `${year}년 ${month}월 ${day}일`;
  }
  return dateStr;
}

export function generateExpenseBriefing({
  receipts,
  lastMonthReceipts = [],
  monthlyBudget = 0,
}: BriefingInput): ExpenseBriefingResult {
  const disclaimer = '이 브리핑은 저장된 지출 기록을 정리한 내용이며, 진단이나 처방을 제공하지 않습니다.';

  // 0. 기록이 없는 경우
  if (!receipts || receipts.length === 0) {
    return {
      totalAmount: 0,
      receiptCount: 0,
      averageAmount: 0,
      summaryMessages: [
        '아직 저장된 병원비 기록이 없습니다.',
        '영수증을 저장하면 월별 지출과 항목별 비중을 확인할 수 있습니다.'
      ],
      insightMessages: [],
      cautionMessages: [],
      disclaimer,
    };
  }

  // 데이터 합산
  const receiptCount = receipts.length;
  let totalAmount = 0;
  let hasMissingItem = false;

  const categoryTotals: Record<string, number> = {};
  const categoryCounts: Record<string, number> = {};
  
  // 가장 최근 병원비 정보 찾기용
  let latestReceipt: any = null;

  receipts.forEach((r) => {
    const amt = getTransactionAmount(r);
    if (!Number.isFinite(amt) || amt <= 0) {
      hasMissingItem = true;
    }
    const safeAmt = Number.isFinite(amt) ? amt : 0;
    totalAmount += safeAmt;

    const cat = r.category || 'OTHER';
    categoryTotals[cat] = (categoryTotals[cat] || 0) + safeAmt;
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;

    if (!latestReceipt || (r.date && r.date > (latestReceipt.date || ''))) {
      latestReceipt = r;
    }
  });

  const averageAmount = Math.round(totalAmount / receiptCount);

  // 카테고리 분석
  const sortedCategories = Object.entries(categoryTotals)
    .map(([cat, amt]) => ({
      category: cat,
      amount: amt,
      ratio: totalAmount > 0 ? Math.round((amt / totalAmount) * 100) : 0,
      count: categoryCounts[cat] || 0,
    }))
    .sort((a, b) => b.amount - a.amount);

  const topCategoryEntry = sortedCategories[0];
  const topCategory = topCategoryEntry?.category;
  const topCategoryAmount = topCategoryEntry?.amount;
  const topCategoryRatio = topCategoryEntry?.ratio;

  const summaryMessages: string[] = [];
  const insightMessages: string[] = [];
  const cautionMessages: string[] = [];

  // 1. 머리말 및 상태별 문구
  if (receiptCount === 1) {
    summaryMessages.push('첫 병원비 기록이 저장되었습니다. 기록이 더 쌓이면 월별 변화와 반복 지출 항목을 함께 확인할 수 있습니다.');
  } else {
    summaryMessages.push('저장된 병원비 기록을 기준으로 지출 흐름을 정리했습니다. 항목별 비중과 최근 지출 변화를 확인해보세요.');
  }

  if (hasMissingItem) {
    summaryMessages.push('일부 항목 정보가 부족해 전체 합계 기준으로만 브리핑했습니다.');
  }

  // 2. 이번 달 요약 상세 문장
  const topCategoryLabel = topCategory ? (CATEGORY_LABELS[topCategory] || topCategory) : '기타';
  summaryMessages.push(
    `이번 달에는 총 ${receiptCount}건의 병원비 기록이 저장되었습니다. 총 지출은 ${totalAmount.toLocaleString()}원이며, 1회 평균 지출은 ${averageAmount.toLocaleString()}원입니다.${
      topCategory ? ` 가장 큰 비중을 차지한 항목은 ${topCategoryLabel}입니다.` : ''
    }`
  );

  if (latestReceipt) {
    const latestAmt = getTransactionAmount(latestReceipt);
    if (Number.isFinite(latestAmt) && latestAmt > 0) {
      summaryMessages.push(
        `가장 최근 병원비 지출은 ${formatDate(latestReceipt.date)}에 발생한 ${latestAmt.toLocaleString()}원(${latestReceipt.title || '일반 지출'})입니다.`
      );
    }
  }

  // 3. 카테고리 분석
  if (topCategory && topCategoryRatio && topCategoryRatio > 0) {
    insightMessages.push(
      `이번 달 지출 중 ${topCategoryLabel}가 가장 큰 비중을 차지했습니다. 전체 지출의 약 ${topCategoryRatio}%가 ${topCategoryLabel}로 기록되었습니다.`
    );
  }

  // 4. 전월 대비 분석
  let monthOverMonthDiff = 0;
  if (lastMonthReceipts && lastMonthReceipts.length > 0) {
    let lastMonthTotal = 0;
    lastMonthReceipts.forEach((r) => {
      const amt = getTransactionAmount(r);
      lastMonthTotal += Number.isFinite(amt) ? amt : 0;
    });

    monthOverMonthDiff = totalAmount - lastMonthTotal;
    if (monthOverMonthDiff > 0) {
      insightMessages.push(`지난달보다 병원비가 ${monthOverMonthDiff.toLocaleString()}원 증가했습니다.`);
    } else if (monthOverMonthDiff < 0) {
      insightMessages.push(`지난달보다 병원비가 ${Math.abs(monthOverMonthDiff).toLocaleString()}원 감소했습니다.`);
    } else {
      insightMessages.push('지난달과 병원비 지출 총액이 동일합니다.');
    }
  } else {
    insightMessages.push('아직 비교할 이전 달 기록이 부족합니다. 영수증 기록이 더 쌓이면 월별 변화도 함께 확인할 수 있습니다.');
  }

  // 5. 예산 대비 브리핑
  let budgetUsageRatio = 0;
  if (monthlyBudget && monthlyBudget > 0) {
    budgetUsageRatio = Math.round((totalAmount / monthlyBudget) * 100);
    if (budgetUsageRatio >= 100) {
      cautionMessages.push(`이번 달 설정 예산의 ${budgetUsageRatio}%를 사용하여 목표 예산을 초과했습니다.`);
    } else if (budgetUsageRatio >= 80) {
      cautionMessages.push(
        `이번 달 설정 예산의 ${budgetUsageRatio}%를 사용했습니다. 추가 지출이 발생하면 예산을 초과할 수 있어 주의가 필요합니다.`
      );
    } else if (budgetUsageRatio < 50) {
      insightMessages.push(`이번 달 설정 예산의 ${budgetUsageRatio}%를 사용해 안정적인 상태입니다.`);
    } else {
      insightMessages.push(`이번 달 설정 예산의 ${budgetUsageRatio}%를 사용했습니다.`);
    }
  } else {
    insightMessages.push('월 예산을 설정하면 이번 달 병원비 사용률을 함께 확인할 수 있습니다.');
  }

  // 6. 반복 지출 감지
  // 카테고리 2회 이상 혹은 특정 병원 2회 이상 등장 시 안내
  const repeatedCategories = sortedCategories.filter(item => item.count >= 2);
  if (repeatedCategories.length > 0) {
    const repeatLabel = CATEGORY_LABELS[repeatedCategories[0].category] || repeatedCategories[0].category;
    cautionMessages.push(
      `최근 기록에서 ${repeatLabel} 항목이 반복적으로 등장하고 있습니다. 다음 진료 때 복약 기간이나 재구매 주기를 함께 확인해볼 수 있습니다.`
    );
  }

  return {
    totalAmount,
    receiptCount,
    averageAmount,
    topCategory,
    topCategoryAmount,
    topCategoryRatio,
    monthOverMonthDiff,
    budgetUsageRatio: monthlyBudget > 0 ? budgetUsageRatio : undefined,
    summaryMessages,
    insightMessages,
    cautionMessages,
    disclaimer,
  };
}
